import React, { useEffect, useState } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Loader2,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Layers,
  FileSpreadsheet,
  Trash2,
  Palette,
  X
} from "lucide-react";

import {
  createTemplate,
  getAllTemplates,
  deleteTemplate,
  getAllGradeSchemes
} from "../../api/reportcard/reportCardApi";
import {
  createTemplatePortal,
  getAllTemplatesPortal,
  deleteTemplatePortal,
  getAllGradeSchemesPortal
} from "../../api/reportcard/reportCardPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import {
  inputCls,
  labelCls,
  pickList,
  toId
} from "../../utils/reportcard/reportCardHelpers";
import {
  useToast
} from "../../context/ToastContext";

const TemplateList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("REPORT_CARD_FULL")
    : true;
  const base = isPortal ? "/staff-panel/report-cards" : "/report-cards";

  const [templates, setTemplates] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    gradeSchemeId: "",
    primaryColor: "#4B3FAE",
    secondaryColor: "#E9E8F7",
    headerSubtitle: "",
    promotionMessage: "Promoted to the next class.",
    failMessage: "Not promoted. Please contact the school.",
    footerNote: "",
    showRank: true,
    showTeacherRemarks: true,
    showPrincipalRemarks: true,
    showGradePoints: false,
  });

  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const [tRes, sRes] = await Promise.all([
        isPortal
          ? getAllTemplatesPortal({ isActive: "true" })
          : getAllTemplates({ isActive: "true" }),
        isPortal
          ? getAllGradeSchemesPortal({ isActive: "true" })
          : getAllGradeSchemes({ isActive: "true" }),
      ]);
      setTemplates(pickList(tRes, ["templates", "data"]));
      const schemeList = pickList(sRes, ["schemes", "data"]);
      setSchemes(schemeList);
      const def = schemeList.find((s) => s.isDefault) || schemeList[0];
      if (def) {
        setForm((f) => ({
          ...f,
          gradeSchemeId: f.gradeSchemeId || toId(def),
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.gradeSchemeId) {
      toast.error("Name and grade scheme are required");
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form, name: form.name.trim() };
      if (isPortal) await createTemplatePortal(payload);
      else await createTemplate(payload);
      toast.success("Template created");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this template?")) return;
    try {
      if (isPortal) await deleteTemplatePortal(id);
      else await deleteTemplate(id);
      toast.success("Template removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5FB] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header navigation bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-200 dark:border-neutral-800/80 pb-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#4B3FAE]">
              Report Cards
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight mt-0.5">
              Templates
            </h1>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              Configure layout branding, colors, and promotion notes.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`${base}/grade-schemes`)}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3.5 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200 shadow-2xs transition-colors"
            >
              <FileSpreadsheet size={16} /> Grade Schemes
            </button>
            <button
              type="button"
              onClick={() => navigate(base)}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3.5 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-200 shadow-2xs transition-colors"
            >
              <Layers size={16} /> Batches
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => setShowForm((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-4 py-2 rounded-xl bg-[#4B3FAE] hover:bg-[#322687] text-white shadow-2xs transition-colors"
              >
                {showForm ? <X size={16} /> : <Plus size={16} />}
                <span>{showForm ? "Cancel" : "New Template"}</span>
              </button>
            )}
          </div>
        </div>
        {/* Missing grade scheme notice */}
        {schemes.length === 0 && !loading && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-xs sm:text-sm text-amber-900 flex items-center justify-between gap-3">
            <span>
              No active grade schemes found. Please create a grade scheme before configuring template cards.
            </span>
            <button
              type="button"
              className="shrink-0 underline font-semibold hover:text-amber-950"
              onClick={() => navigate(`${base}/grade-schemes`)}
            >
              Create Grade Scheme
            </button>
          </div>
        )}

        {/* Create form drawer / card */}
        {showForm && canManage && (
          <form
            onSubmit={handleCreate}
            className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-5 sm:p-6 shadow-xs space-y-5"
          >
            <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-3">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Add Template Configuration
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Template Name *</label>
                <input
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Standard Terminal Card"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Grade Scheme *</label>
                <select
                  className={inputCls}
                  value={form.gradeSchemeId}
                  onChange={(e) =>
                    setForm({ ...form, gradeSchemeId: e.target.value })
                  }
                  required
                >
                  <option value="">Select Scheme</option>
                  {schemes.map((s) => (
                    <option key={toId(s)} value={toId(s)}>
                      {s.name}
                      {s.isDefault ? " (Default)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-[#F5F5FB] px-4 py-3 flex items-center justify-between gap-3">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  School name, logo, and address are pulled automatically
                  from your <span className="font-medium text-neutral-700 dark:text-neutral-200">School Settings</span> — no need to re-enter them here.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(isPortal ? "/staff-panel/school" : "/school/settings")}
                  className="shrink-0 text-xs font-semibold text-[#4B3FAE] hover:text-[#322687] underline whitespace-nowrap"
                >
                  Edit School Info
                </button>
              </div>

              <div>
                <label className={labelCls}>Header Subtitle</label>
                <input
                  className={inputCls}
                  value={form.headerSubtitle}
                  onChange={(e) =>
                    setForm({ ...form, headerSubtitle: e.target.value })
                  }
                  placeholder="e.g. Annual Progress Report"
                />
              </div>

              <div />

              <div>
                <label className={labelCls}>Primary Theme Color</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    className="h-9 w-12 border border-neutral-200 dark:border-neutral-800 rounded-lg cursor-pointer p-0.5"
                    value={form.primaryColor}
                    onChange={(e) =>
                      setForm({ ...form, primaryColor: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    className={inputCls}
                    value={form.primaryColor}
                    onChange={(e) =>
                      setForm({ ...form, primaryColor: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Secondary Theme Color</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    className="h-9 w-12 border border-neutral-200 dark:border-neutral-800 rounded-lg cursor-pointer p-0.5"
                    value={form.secondaryColor}
                    onChange={(e) =>
                      setForm({ ...form, secondaryColor: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    className={inputCls}
                    value={form.secondaryColor}
                    onChange={(e) =>
                      setForm({ ...form, secondaryColor: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className={labelCls}>Promotion Message</label>
                <input
                  className={inputCls}
                  value={form.promotionMessage}
                  onChange={(e) =>
                    setForm({ ...form, promotionMessage: e.target.value })
                  }
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelCls}>Fail Message</label>
                <input
                  className={inputCls}
                  value={form.failMessage}
                  onChange={(e) =>
                    setForm({ ...form, failMessage: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="pt-2">
              <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 block mb-2">
                Display Options
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-[#F5F5FB] p-3 rounded-xl border border-neutral-200 dark:border-neutral-800">
                {[
                  ["showRank", "Show Rank"],
                  ["showTeacherRemarks", "Teacher Remarks"],
                  ["showPrincipalRemarks", "Principal Remarks"],
                  ["showGradePoints", "Grade Points"],
                ].map(([k, lab]) => (
                  <label key={k} className="inline-flex items-center gap-2 text-xs font-medium text-neutral-700 dark:text-neutral-200 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded-md border-slate-300 dark:border-neutral-700 text-[#4B3FAE] focus:ring-[#4B3FAE]"
                      checked={form[k]}
                      onChange={(e) =>
                        setForm({ ...form, [k]: e.target.checked })
                      }
                    />
                    <span>{lab}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200 dark:border-neutral-800">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-xs sm:text-sm font-medium px-4 py-2 rounded-xl border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !form.gradeSchemeId}
                className="inline-flex items-center gap-2 bg-[#4B3FAE] hover:bg-[#322687] text-white text-xs sm:text-sm font-medium px-5 py-2 rounded-xl transition-colors disabled:opacity-50"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                <span>{submitting ? "Saving..." : "Create Template"}</span>
              </button>
            </div>
          </form>
        )}

        {/* Template List */}
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-xs overflow-hidden">
          {loading ? (
            <div className="p-16 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-[#4B3FAE]" />
              <span className="text-xs">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="p-12 text-center text-neutral-400 dark:text-neutral-500">
              <Palette size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-xs sm:text-sm font-medium">No templates created yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-neutral-800">
              {templates.map((t) => (
                <li
                  key={toId(t)}
                  className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-[#F5F5FB]/60 transition-colors"
                >
                  <div className="flex items-center gap-3.5">
                    <div
                      className="w-10 h-10 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-2xs shrink-0"
                      style={{
                        background: `linear-gradient(135deg, ${
                          t.primaryColor || "#4B3FAE"
                        }, ${t.secondaryColor || "#E9E8F7"})`,
                      }}
                    />
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">
                        {t.name}
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        Scheme:{" "}
                        <span className="font-medium text-neutral-700 dark:text-neutral-200">
                          {t.gradeSchemeId?.name ||
                            (typeof t.gradeSchemeId === "string"
                              ? t.gradeSchemeId
                              : "—")}
                        </span>
                      </p>
                    </div>
                  </div>

                  {canManage && (
                    <button
                      type="button"
                      onClick={() => handleDelete(toId(t))}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-danger-600 dark:text-danger-500 hover:text-red-700 bg-red-50 hover:bg-red-100/80 px-3 py-1.5 rounded-lg transition-colors self-end sm:self-auto"
                    >
                      <Trash2 size={14} />
                      <span>Deactivate</span>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateList;